import React, { useEffect, useState } from 'react';
import { Select } from '@/components/atomics/';
import Pagebase from '@/components/organism/Pagebase';
import styles from './ListFeedback.module.scss';
import Datatable from '@/components/atomics/Datatable';
import Chips from '@/components/atomics/Chips';
import { useDownloadFeedback, useFeedback } from '@/hooks/useFeedback';
import FeedbackSummary from '@/components/organism/Feedback/FeedbackSummary';
import FeedbackData from '@/types/Feedback';
import FeedbackAction from '@/components/organism/Feedback/FeedbackAction';
import DaterangePickerProps from '@/components/atomics/DaterangePicker/type';
import moment from 'moment';

const className = 'list-feedback';

const ListFeedback: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [feedback, setFeedback] = useState<FeedbackData>();
  const [filterNPS, setFilterNPS] = useState<string | undefined>(undefined);
  const [filterRating, setFilterRating] = useState<string | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  useEffect(() => {
    refetch();
  }, [startDate, endDate, filterNPS, filterRating, filterStatus, page, pageSize]);

  const { data = {
    list: [],
    meta: {
      page: 1,
      totalPage: 1,
    }
  }, refetch, isLoading } = useFeedback({ startDate, endDate, filterNPS, filterRating, filterStatus, page, pageSize });

  const { data: dataDownload, refetch: refetchDownload, isLoading: isLoadingDownload } = useDownloadFeedback({ startDate, endDate, filterNPS, filterRating, filterStatus, page, pageSize: data?.meta?.totalData });

  useEffect(() => {
    if (dataDownload?.list?.length > 0 && isLoadingDownload === false) {
      const keys = Object.keys(dataDownload.list[0]);
      interface CsvRow {
        [key: string]: string | number | boolean | null | undefined;
      }

      const csvRows: string[] = [
        keys.join(','),
        ...dataDownload.list.map((row: CsvRow) =>
          keys.map((k: string) => `"${(row[k] ?? '').toString().replace(/"/g, '""')}"`).join(',')
        ),
      ];
      const csvString = csvRows.join('\n');

      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'feedback.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [isLoadingDownload, dataDownload?.list]);

  const handleChangeDate = (date: DaterangePickerProps) => {
    setStartDate(moment(date?.startDate).format('YYYY-MM-DD'));
    setEndDate(moment(date?.endDate).format('YYYY-MM-DD'));
  };

  const renderChips = (status: string) => {
    switch (status) {
      case 'todo':
        return <Chips className={styles[className + '__chips']} label="To Do" variant="warning" />;
      case 'done':
        return <Chips className={styles[className + '__chips']} label="Done" variant="success" />;
    }
  };

  const renderEmptyData = () => (
    <>
      <p>
        <img src="/images/icon-nodata.png" alt="No data" />
      </p>
      <label>
        Belum ada data NPS & Rating APPS yang masuk
      </label>
    </>
  );

  return (
    <Pagebase className={styles[className]} onChangeDate={handleChangeDate} onDownload={() => {
      refetchDownload();
    }}
      title="NPS & Rating Apps">
      <FeedbackSummary className={styles[className + '__summary']}
        startDate={startDate}
        endDate={endDate}
      />
      <div className={styles[className + '__card']}>
        <div className={styles[className + '__card__form']}>
          <label>Filter Berdasarkan:</label>
          <Select
            name="nps"
            placeholder="Pilih Score"
            onChange={e => setFilterNPS(e.target.value)}
            options={[
              {
                text: 'Semua NPS',
                value: 'all'
              },
              {
                text: 'Score 1',
                value: '1'
              },
              {
                text: 'Score 2',
                value: '2'
              },
              {
                text: 'Score 3',
                value: '3'
              },
              {
                text: 'Score 4',
                value: '4'
              },
              {
                text: 'Score 5',
                value: '5'
              },
              {
                text: 'Score 6',
                value: '6'
              },
              {
                text: 'Score 7',
                value: '7'
              },
              {
                text: 'Score 8',
                value: '8'
              },
              {
                text: 'Score 9',
                value: '9'
              },
              {
                text: 'Score 10',
                value: '10'
              }
            ]}
            value={filterNPS}
          />
          <Select
            name="rating"
            placeholder="Pilih Rating"
            onChange={e => setFilterRating(e.target.value)}
            options={[
              {
                text: 'Semua Rating',
                value: 'all'
              },
              {
                text: 'Rating 1',
                value: '1'
              },
              {
                text: 'Rating 2',
                value: '2'
              },
              {
                text: 'Rating 3',
                value: '3'
              },
              {
                text: 'Rating 4',
                value: '4'
              },
              {
                text: 'Rating 5',
                value: '5'
              }
            ]}
            value={filterRating}
          />
          <Select
            name="status"
            placeholder="Pilih Status"
            onChange={e => setFilterStatus(e.target.value)}
            options={[
              {
                text: 'Semua Status',
                value: 'all'
              },
              {
                text: 'To Do',
                value: 'todo'
              },
              {
                text: 'Done',
                value: 'done'
              }
            ]}
            value={filterStatus}
          />
        </div>
        <Datatable
          columns={[
            { label: 'User ID', key: 'userId' },
            { label: 'Nama', key: 'username' },
            { label: 'Channel', key: 'channel' },
            { label: 'NPS', key: 'score' },
            { label: 'Detail Feedback NPS', key: 'nps_comment'},
            { label: 'Rating', key: 'stars' },
            { label: 'Feedback', key: 'feedbackScope' },
            { label: 'Detail Feedback Rating', key: 'feedback'},
            { label: 'Status', key: 'status' },
            { label: 'Action', key: 'action' },
          ]}
          data={data?.list?.length > 0 ? data?.list?.map((item: FeedbackData, index: number) => ({
            ...item,
            channel: 'EZY',
            status: renderChips(item.reviewStatus),
            action: <div className={styles[className + '__action']}
              onClick={() => {
                setFeedback(item);
              }}>
              Lihat Detail
            </div>
          })) : []}
          emptyData={renderEmptyData()}
          isLoading={isLoading}
          page={data.meta.page}
          totalPage={data.meta.totalPages}
          onChangePage={setPage}
          onChangeSize={setPageSize}
        />
      </div>
      {feedback && <FeedbackAction feedback={feedback} onCancel={() => setFeedback(undefined)}
        onSubmit={() => {
          setFeedback(undefined);
          refetch();
        }} />}
    </Pagebase>
  );
};

export default ListFeedback;